import { FilterStateItem, ConnectProto } from "@racoon/api";
import { useCallback, useMemo, useState, useEffect, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { Namespace, useT } from "@racoon/i18n";

const { OrganizationType } = ConnectProto;

export const tabToType: Record<string, ConnectProto.OrganizationType | undefined> = {
  all: undefined,
  providers: OrganizationType.SERVICE_PROVIDER,
  receivers: OrganizationType.SERVICE_RECEIVER,
  disposal: OrganizationType.DISPOSAL_SITE_OPERATOR,
  government: OrganizationType.GOVERNMENT,
  raccoon: OrganizationType.RACCOONAI,
};

export const useOrganizationsUrlState = (totalItems = 0, pageSize = 0) => {
  const [sp, setSp] = useSearchParams();
  const t = useT();

  const states = useMemo<FilterStateItem[]>(
    () => [
      { key: t(Namespace.Employees, "tabs.all"), value: "all" },
      { key: t(Namespace.Organizations, "tabs.providers"), value: "providers" },
      { key: t(Namespace.Organizations, "tabs.receivers"), value: "receivers" },
      { key: t(Namespace.Organizations, "tabs.disposal"), value: "disposal" },
      { key: t(Namespace.Organizations, "tabs.government"), value: "government" },
    ],
    [t],
  );

  const tabParam = sp.get("tab") || "all";
  const queryParam = sp.get("q") || "";
  const pageParam = parseInt(sp.get("page") || "1", 10);
  const currentPage = Number.isFinite(pageParam) && pageParam > 0 ? pageParam : 1;

  const [searchValue, setSearchValue] = useState(queryParam);

  useEffect(() => {
    setSearchValue(queryParam);
  }, [queryParam]);

  const selectedState = states.find((s) => s.value === tabParam) || states[0];

  const patchURL = useCallback(
    (updates: Record<string, string | null>) => {
      setSp((prev) => {
        const next = new URLSearchParams(prev);
        Object.entries(updates).forEach(([key, val]) => {
          if (val != null) next.set(key, val);
          else next.delete(key);
        });
        return next;
      });
    },
    [setSp],
  );

  // Reset to page 1 when pageSize changes (zoom / resize)
  const prevPageSizeRef = useRef(pageSize);
  useEffect(() => {
    if (prevPageSizeRef.current !== 0 && pageSize !== 0 && prevPageSizeRef.current !== pageSize) {
      patchURL({ page: null });
    }
    prevPageSizeRef.current = pageSize;
  }, [pageSize, patchURL]);

  const searchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    return () => {
      if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    };
  }, []);

  const onStateChange = (v: FilterStateItem) => {
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    setSearchValue("");
    patchURL({ tab: v.value, page: null, q: null });
  };

  const onSearchChange = (val: string) => {
    setSearchValue(val);
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => {
      patchURL({ q: val || null, page: null });
    }, 1000);
  };

  const onPageChange = (page: number) => {
    patchURL({ page: String(page) });
  };

  const totalPages = pageSize > 0 ? Math.ceil(totalItems / pageSize) : 0;

  return {
    states,
    selectedState,
    onStateChange,
    searchValue,
    onSearchChange,
    onPageChange,
    currentPage,
    totalPages,
  };
};
